import React, { useEffect, useState } from "react";
import { Card, Form, Button, Image, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import authemail from "assets/images/auth/email.png";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MecordColors } from "Constants/Color";
import { Axios } from "api/axios";
import { useDispatch } from "react-redux";
import { resetForgotPassword, sendForgotPasswordOTP, verifyForgotPasswordOTP } from "slices/auth/login/thunk";

const url = process.env.REACT_APP_LOCAL_BASE_URL;

// Step type
type Step = "email" | "otp" | "resetPassword";

const ForgotPassword = () => {
  const [step, setStep] = useState<Step>("email");
  const [cooldown, setCooldown] = useState(0);
  const [timeActive, setTimeActive] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [emailSent, setEmailSent] = useState("");
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<any>();

  // Cooldown timer for OTP resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timeActive && cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            setTimeActive(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timeActive, cooldown]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      otp: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Please enter your email"),
      otp:
        step === "otp"
          ? Yup.string()
              .required("Please enter OTP")
              .matches(/^\d{6}$/, "OTP must be exactly 6 digits")
          : Yup.string(),
      password:
        step === "resetPassword"
          ? Yup.string()
              .required("Please enter a new password")
              .test(
                "not-empty-spaces",
                "Please enter a new password",
                (value) => !!value?.trim(),
              )
              .min(8, "Password must be at least 8 characters")
          : Yup.string(),
      confirmPassword:
        step === "resetPassword"
          ? Yup.string()
              .required("Please confirm your password")
              .test(
                "not-empty-spaces",
                "Please confirm your password",
                (value) => !!value?.trim(),
              )
              .oneOf([Yup.ref("password")], "Passwords must match")
          : Yup.string(),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
  setIsSubmitting(true);

  if (step === "email") {
    if (timeActive && cooldown > 0) {
      toast.warning(
        `Please wait ${Math.floor(cooldown / 60)}m ${cooldown % 60}s`
      );
      return;
    }

    await dispatch(sendForgotPasswordOTP(values.email));
    toast.success("OTP sent to your email");

    setEmailSent(values.email);
    setStep("otp");
    setCooldown(300);
    setTimeActive(true);
  }

  else if (step === "otp") {
    await dispatch(verifyForgotPasswordOTP(emailSent, values.otp));
    toast.success("OTP verified successfully");
    setStep("resetPassword");
  }

  else if (step === "resetPassword") {
    await dispatch(resetForgotPassword(emailSent, values.password));
    toast.success("Password reset successfully");

    setTimeout(() => navigate("/login"), 1500);
  }
} catch (error: any) {
  toast.error(error);
} finally {
  setIsSubmitting(false);
}
    },
  });

  return (
    <div className="col-xxl-6 mx-auto">
      <ToastContainer />
      <Card className="card mb-0 border-0 shadow-none">
        <Card.Body className="p-sm-5 m-lg-4">
          <div className="text-center mt-2">
            <h5 className="fs-3xl">Forgot Password?</h5>
            <div className="pb-4">
              <Image src={authemail} alt="Email icon" className="avatar-md" />
            </div>
          </div>

          <Alert
            className="border-0 alert-warning text-center mb-2 mx-2"
            role="alert"
          >
            {step === "email" &&
              "Enter your email and we will send a 6-digit OTP to it."}
            {step === "otp" && "Enter the OTP sent to your email."}
            {step === "resetPassword" && "Set your new password."}
          </Alert>

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            {/* Email Step */}
            {step === "email" && (
              <div className="mb-4">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter Email"
                  name="email"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  isInvalid={
                    !!validation.errors.email && !!validation.touched.email
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {validation.errors.email}
                </Form.Control.Feedback>
              </div>
            )}

            {/* OTP Step */}
            {step === "otp" && (
              <div className="mb-4">
                <Form.Label>OTP</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter OTP"
                  name="otp"
                  value={validation.values.otp}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  isInvalid={
                    !!validation.errors.otp && !!validation.touched.otp
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {validation.errors.otp}
                </Form.Control.Feedback>
                <Button
                  variant="link"
                  disabled={timeActive}
                  className="mt-2 p-0"
                  onClick={async () => {
                    setIsSubmitting(true);
                    try {
                      const res: any = await axios.post(
                        `${url}/users/forgot-password/`,
                        {
                          email: emailSent,
                        }
                      );
                      toast.success("OTP resent successfully");
                      setCooldown(300);
                      setTimeActive(true);
                    } catch {
                      toast.error("Unable to resend OTP");
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {timeActive
                    ? `Resend OTP in ${Math.floor(cooldown / 60)}:${
                        cooldown % 60
                      }`
                    : "Resend OTP"}
                </Button>
              </div>
            )}

            {/* Reset Password Step */}
            {step === "resetPassword" && (
              <>
                <Form.Label>New Password</Form.Label>
                <div className="position-relative auth-pass-inputgroup mb-3">
                  <Form.Control
                    type={passwordShow ? "text" : "password"}
                    placeholder="Enter new password"
                    name="password"
                    value={validation.values.password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    isInvalid={
                      !!validation.errors.password &&
                      !!validation.touched.password
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {validation.errors.password}
                  </Form.Control.Feedback>
                  <Button
                    variant="link"
                    className="position-absolute end-0 top-0 mt-1 me-2 text-decoration-none text-muted"
                    type="button"
                    onClick={() => setPasswordShow(!passwordShow)}
                  >
                    <i
                      className={
                        passwordShow ? "ri-eye-fill" : "ri-eye-off-fill"
                      }
                    ></i>
                  </Button>
                </div>

                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type={passwordShow ? "text" : "password"}
                  placeholder="Confirm password"
                  name="confirmPassword"
                  value={validation.values.confirmPassword}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  isInvalid={
                    !!validation.errors.confirmPassword &&
                    !!validation.touched.confirmPassword
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {validation.errors.confirmPassword}
                </Form.Control.Feedback>
              </>
            )}

            <div className="text-center mt-4">
              <Button
                className="w-100"
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: MecordColors.PrimaryButtonBGColor,
                  borderColor: MecordColors.PrimaryButtonBGColor,
                  color: "#fff",
                  height: "42px",
                  fontWeight: 500,
                }}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Loading...
                  </>
                ) : step === "email" ? (
                  "Send OTP"
                ) : step === "otp" ? (
                  "Verify OTP"
                ) : (
                  "Reset Password"
                )}
              </Button>
            </div>

            {step === "email" && (
              <div className="mt-4 text-center">
                <p className="mb-0">
                  Wait, I remember my password...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-primary text-decoration-underline"
                  >
                    Click here
                  </Link>
                </p>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ForgotPassword;
